import * as Sentry from "@sentry/browser";

import { QueryClient } from "react-query";
import { AnyAction, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";

import { toast } from "@/design-system/components/Toast/Toast";

import {
  AccountMetadata,
  SET_ACCOUNT_METADATA,
  SET_CURRENT_ENVIRONMENT,
} from "../reducers/environments";
import { PropertySensitivityLevels } from "../types/global";
import { makeRequest } from "../util";
import { getSetLoginPageAction } from "./loginPage";
import { getCurrentUser, signOut } from "@/auth/firebase";

// Not handling Modern Health here because they are not currently using SSO.
// Not handling Inova here because their account isn't restricted to SSO users.
const ssoEmailToLoginPage = new Map([
  [/bcbsnc\.com$/, "/login/sso/bcbsnc"],
  [/betabionics\.com$/, "/login/sso/betabionics"],
  [/alpine-physicians\.com$/, "/login/sso/alpine"],
  [/caresource\.com$/, "/login/sso/caresource"],
  [/eyecare-partners\.com$/, "/login/sso/eyecare-partners"],
  [/freshpaint\.io$/, "/login/sso/jumpcloud-sso-test"],
  [/heartland\.com$/, "/login/sso/heartland"],
  [/jefferson\.edu$/, "/login/sso/jefferson"],
  [/rogersbh\.org$/, "/login/sso/rogersbh"],
  [/unchealth\.unc\.edu$/, "/login/sso/unc-health"],
  [/virtahealth\.com$/, "/login/sso/virtahealth"],
  [/yale\.edu$/, "/login/sso/yale-school-of-medicine"],
  [/ynhh\.org$/, "/login/sso/yale-new-haven-health"],
  [/prismahealth\.org$/, "/login/sso/prisma-health"],
  [/gohealthuc\.com$/, "/login/sso/gohealth"],
  [/geha\.com$/, "/login/sso/geha"],
  [
    /(kphhealthcareservices\.com|kphhealthcare\.onmicrosoft\.com|proactrx\.com)$/,
    "/login/sso/kphhealthcare",
  ],
  [/tempus\.com$/, "/login/sso/tempus"],
  [/evernorth\.com$/, "/login/sso/cigna"],
  [/nuvancehealth\.org$/, "/login/sso/nuvancehealth"],
  [/warbyparker\.com$/, "/login/sso/warby-parker"],
  [/lifemd\.com$/, "/login/sso/lifemd"],
  [/mediweightloss\.com$/, "/login/sso/medi-weightloss"],
  [/functionhealth\.com$/, "/login/sso/functionhealth"],
  [/meridianhs\.org$/, "/login/sso/meridianhs"],
  [/medtronic\.com/, "/login/sso/medtronic"],
  [/imail\.org/, "/login/sso/intermountain"],
  [/valtech\.com$/, "/login/sso/baystate"],
  [/baystatehealth\.org$/, "/login/sso/baystate"],
  [/cignahealthcare\.com$/, "/login/sso/hcsc"],
  [/hcsc\.com$/, "/login/sso/hcsc"],
  [/forefrontderm\.com$/, "/login/sso/forefrontderm"],
  [/soleranetwork\.com$/, "/login/sso/solera"],
  [/boulder\.care$/, "/login/sso/bouldercare"],
  [/(nmhs\.net|mergeworld\.com)$/, "/login/sso/nmhs"],
  [/equip\.health$/, "/login/sso/equiphealth"],
  [/bronsonhg\.org$/, "/login/sso/bronsonhg"],
  [/(womenscareusa\.com|womenscarefl\.com)$/, "/login/sso/womenscareusa"],
  [/growtherapy\.com$/, "/login/sso/growtherapy"],
  [/lyrahealth\.com$/, "/login/sso/lyrahealth"],
  [/bendhealth\.com$/, "/login/sso/bendhealth"],
  [/mvphealthcare\.com$/, "/login/sso/mvp-healthcare"],
  [/vcuhealth\.org$/, "/login/sso/vcuhealth"],
  [
    /(americanfamilycare|afcurgentcare|ignitevisibility)\.com$/,
    "/login/sso/afcurgentcare",
  ],
  [/healthfirst\.org$/, "/login/sso/healthfirst"],
]);

// Handle an "sso_required" response from the backend. This happens when someone
// tries to log in to an account that requires SSO via some other provider (e.g.
// email link, google, or github). The solution is to sign them out and redirect
// them to the correct SSO login page.
function handleSSORequired(dispatch: Dispatch, err: any) {
  const email = getCurrentUser()?.email;
  if (!email) {
    return;
  }

  signOut();

  const loginURI = mapEmailToSSOLoginPage(email);
  if (loginURI) {
    dispatch(getSetLoginPageAction(loginURI));
  } else {
    console.log("SSO is required for this account");
    Sentry.captureException(err);
  }
}

function mapEmailToSSOLoginPage(email) {
  for (let [regex, loginPage] of ssoEmailToLoginPage) {
    if (regex.test(email)) {
      return loginPage;
    }
  }

  return null;
}

// Handle an "sso_forbidden" response from the backend. This happens when an SSO
// provider tries to authenticate as a user from an email domain that we haven't
// approved. For example if a customer's SSO provider tried to authenticate as a
// Freshpaint employee. The solution is to sign them out and redirect to the
// main login page.
function handleSSOForbidden(dispatch: Dispatch) {
  const email = getCurrentUser()?.email;
  if (!email) {
    return;
  }

  signOut();

  dispatch(getSetLoginPageAction("/login"));

  toast.error(
    "SSO authentication not allowed for this user. Please sign in again.",
  );
}

export const fetchAccountMetadata = () => {
  return async (dispatch: Dispatch) => {
    return makeRequest<AccountMetadata>("account_metadata", {})
      .then((response) => {
        const accountID =
          response.data.projects.length > 0
            ? response.data.projects[0].accountID
            : null;

        const payload = {
          ...response.data,
          accountID: accountID,
        };

        dispatch({ type: SET_ACCOUNT_METADATA, payload });
      })
      .catch((err) => {
        if (
          err.response &&
          err.response.data.error_message === "sso_required"
        ) {
          handleSSORequired(dispatch, err);
        } else if (
          err.response &&
          err.response.data.error_message === "sso_forbidden"
        ) {
          handleSSOForbidden(dispatch);
        }
      });
  };
};

export const setCurrentEnvironment = (
  envID: string,
  queryClient: QueryClient,
) => {
  return async (dispatch: Dispatch) => {
    // Invalidate all react-query query caches when the environment changes. While heavy-handed,
    // this has the upside of ensuring that we don't have any stale data in the cache that should not
    // be rendered for the new environment selected.
    queryClient.removeQueries();

    dispatch({ type: SET_CURRENT_ENVIRONMENT, payload: envID });
  };
};

export const updateProjectPhiAllowlist = (
  allowListType: string,
  newSensitivityLevels: PropertySensitivityLevels,
): ThunkAction<Promise<void>, any, unknown, AnyAction> => {
  return async (dispatch) => {
    await makeRequest("update_phi_allowlist", {
      sensitivity_type: allowListType,
      sensitivity_levels: newSensitivityLevels,
    });

    // Because this state is in a few places in the 'environments' store
    // ('accountMetadata.projects' and 'currentEnvironment'), just refresh the
    // account metadata.
    await dispatch(fetchAccountMetadata());
  };
};
